import { useMutation } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { QueryKey } from '@/Config/api/queryKey';
import {
  CreditTopupAutoReload as CreditTopupAutoReloadDto,
  CreditTopupAutoReloadInput,
  CreditTopupCardSetupInput,
  CreditTopupCheckoutUrl,
  CreditTopupEntrypoint,
  CreditTopupOffer,
} from '@/Domain/api/Subscription/dto/CreditTopup.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { AutoReloadSwitchRow } from '@/Domain/components/Subscription/CreditTopup/AutoReloadSwitchRow';
import { saveCreditTopupCheckoutAttribution } from '@/Domain/components/Subscription/CreditTopup/creditTopupAnalytics';
import { recurringTaxNote } from '@/Domain/components/Subscription/CreditTopup/recurringTaxNote';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type Props = {
  offer: CreditTopupOffer;
  offerCardSetup: boolean;
  entrypoint: CreditTopupEntrypoint;
  canEnable?: boolean;
};

const SWITCH_ID = 'credit-topup-auto-reload';

export const CreditTopupAutoReload = ({
  offer,
  offerCardSetup,
  entrypoint,
  canEnable = true,
}: Props) => {
  const { t } = useTranslation('subscription_page');
  const { capture } = usePostHogAnalytics();
  const { auto_reload: autoReload } = offer;

  const { mutate: setAutoReload, isPending } = useMutation<
    CreditTopupAutoReloadDto,
    unknown,
    CreditTopupAutoReloadInput
  >({
    mutationKey: [QueryKey.subscription.creditTopupAutoReload],
    onSuccess: (_data, { enabled }) => {
      capture(PostHogEventEnum.CreditTopupAutoReloadToggled, { enabled });
      toast.success(
        t(enabled ? 'topup_panel.auto_reload.turned_on' : 'topup_panel.auto_reload.turned_off'),
      );
    },
    onError: () => toast.error(t('topup_panel.auto_reload.error')),
  });

  const { mutate: startCardSetup, isPending: isSavingCard } = useMutation<
    CreditTopupCheckoutUrl,
    unknown,
    CreditTopupCardSetupInput
  >({
    mutationKey: [QueryKey.subscription.creditTopupCardSetup],
    onSuccess: ({ url }, input) => {
      saveCreditTopupCheckoutAttribution({
        flow: 'card_setup',
        entrypoint: input.entrypoint,
        auto_reload: true,
        has_balance: offer.topup_credits > 0,
      });
      capture(PostHogEventEnum.CreditTopupCardSetupStarted, {
        entrypoint: input.entrypoint,
        has_balance: offer.topup_credits > 0,
        replacing_card: autoReload.available,
      });
      window.location.href = url;
    },
    onError: (_error, input) => {
      capture(PostHogEventEnum.CreditTopupCardSetupCreationFailed, {
        entrypoint: input.entrypoint,
        has_balance: offer.topup_credits > 0,
        replacing_card: autoReload.available,
      });
      toast.error(t('topup_panel.auto_reload.error'));
    },
  });

  const credits = offer.credits.toLocaleString();
  const threshold = autoReload.threshold_credits.toLocaleString();
  const cardBrand = autoReload.card_brand.charAt(0).toUpperCase() + autoReload.card_brand.slice(1);

  const onCheckedChange = (enabled: boolean) => {
    if (enabled && !canEnable) return;
    if (enabled && !autoReload.available) return;
    setAutoReload({ enabled });
  };

  if (offerCardSetup && !autoReload.available) {
    return (
      <AutoReloadSwitchRow
        id={SWITCH_ID}
        checked={false}
        disabled={isSavingCard}
        pending={isSavingCard}
        label={t('topup_panel.auto_reload.state_off')}
        description={t('topup_panel.auto_reload.save_card_hint')}
        switchTestId="credit-topup-auto-reload-switch"
        onCheckedChange={(enabled) => enabled && startCardSetup({ entrypoint })}
      />
    );
  }

  if (!autoReload.available && !autoReload.enabled) return null;

  return (
    <AutoReloadSwitchRow
      id={SWITCH_ID}
      checked={autoReload.enabled}
      disabled={isPending || (!canEnable && !autoReload.enabled)}
      pending={isPending}
      label={t(
        autoReload.enabled
          ? 'topup_panel.auto_reload.state_on'
          : 'topup_panel.auto_reload.state_off',
      )}
      description={
        !canEnable
          ? t('topup_panel.auto_reload.unavailable_label')
          : t(
              autoReload.enabled
                ? 'topup_panel.auto_reload.on_description'
                : 'topup_panel.auto_reload.off_description',
              {
                credits,
                price: offer.price_usd,
                threshold,
                tax: recurringTaxNote(t('pricing.excl_vat'), autoReload.tax_included),
              },
            )
      }
      descriptionTestId="credit-topup-auto-reload-status"
      switchTestId="credit-topup-auto-reload-switch"
      onCheckedChange={onCheckedChange}
      footer={
        canEnable &&
        autoReload.available && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-4">
            <span
              className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground"
              data-testid="credit-topup-saved-card"
            >
              <CreditCard className="size-4 shrink-0" aria-hidden="true" />
              {autoReload.card_last4 && (
                <span className="truncate">
                  {t('topup_panel.auto_reload.card_on_file', {
                    card: cardBrand,
                    last4: autoReload.card_last4,
                  })}
                </span>
              )}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              loading={isSavingCard}
              onClick={() => startCardSetup({ entrypoint })}
              data-testid="credit-topup-change-card-button"
            >
              {t(
                autoReload.enabled
                  ? 'topup_panel.auto_reload.change_card'
                  : 'topup_panel.auto_reload.change_card_and_turn_on',
              )}
            </Button>
          </div>
        )
      }
    />
  );
};
